import React, { useState, useEffect, useCallback } from "react";
import { Button, Loading, SearchInput } from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import PageHeader from "components/PageHeader/";
import PaginationButton from "../PaginationButton";
import api from "lib/api";
import usePagedData from "hooks/usePagedData";
import DashboardLayout from "../Layouts/DashboardLayout";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";

interface Activity {
	name: string;
	description: string;
	id: string;
}

function Activities() {
	const history = useHistory();
	const { t } = useTranslation();

	const [init, setInit] = useState(false);
	const [searchBox, setSearchBox] = useState("");
	const [searchValue, setSearchValue] = useState("");

	// Pagination for activities list
	const { state, setData, ready, loading, searching, addPage } =
		usePagedData();

	/**
	 * Search change
	 */
	const onChangeEvent = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Search input valued change to "${value}"`,
			});

			if (value !== "") {
				setSearchBox(value);
			} else {
				setSearchBox(value);
				setSearchValue(value);
			}
		},
		[]
	);

	/**
	 * Load list of activities on first load
	 */
	useEffect(() => {
		if (!init) {
			// Load activities from API
			api.workflow
				.getDefinitions()
				.then(results => {
					setData(results);
					ready();
				})
				.catch(e => console.error(e));

			setInit(true);
		}
	}, [init, ready, setData]);

	/**
	 * Load new list of activities when search term has been changed
	 */
	useEffect(() => {
		if (init) {
			searching();
			// Load activities from API with search query
			api.workflow
				.getDefinitions({ limit: 20, offset: 0, search: searchValue })
				.then(results => {
					setData(results);
					ready();
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue]);

	/**
	 * Add new activities to page when paged
	 */
	const page = useCallback(async () => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Page activities",
		});

		loading();
		try {
			const res = await api.workflow.getDefinitions({
				limit: state.limit,
				offset: state.offset,
				search: state.search,
			});
			addPage(res);
			ready();
		} catch (e) {
			console.error(e);
		}
	}, [addPage, loading, ready, state.limit, state.offset, state.search]);

	/**
	 * Handle starting an activity
	 */
	const startActivityClick = useCallback(
		(id: string) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: `Start activity ${id}`,
			});

			history.push(`/activities/start/${id}`);
		},
		[history]
	);

	return (
		<DashboardLayout>
			<div className="container" aria-live="polite">
				<PageHeader title={t("main.activities.pageTitle")} />
				<div className="search-activities">
					<div className="search-bar">
						<SearchInput
							label={t("main.text.search")}
							onSearch={() => setSearchValue(searchBox)}
							onChange={onChangeEvent}
							value={searchBox}
							placeholder={"Filter by search..."}
						/>
					</div>
				</div>
				<div className="activities-body--desktop">
					{(state.loading && !state.hasMore) || state.searching ? (
						<Loading className="mt-4 elem__loader--md" />
					) : state.results.length ? (
						state.results.map(
							(activity: Activity, index: number) => (
								<div
									className="card activities-card"
									key={`activity__${index}`}>
									<div className="card-body activities__body">
										<div>
											<h2 className="activities__title">
												{activity.name}
											</h2>
											<div className="activities__desc">
												{activity.description}
											</div>
										</div>
										<Button
											className="btn-primary"
											onClick={() =>
												startActivityClick(activity.id)
											}
											label={t("main.labels.start")}
											aria-label={
												t("main.labels.start") +
												" " +
												activity.name
											}
										/>
									</div>
								</div>
							)
						)
					) : (
						!state.loading && (
							<p className="activities__empty">
								{t(
									searchValue.trim() !== ""
										? "main.activities.noSearch"
										: "main.activities.noActivities"
								)}
							</p>
						)
					)}
					{state.hasMore && !state.searching ? (
						<PaginationButton
							pageType="activities"
							loading={state.loading}
							page={page}
							hasMore={state.hasMore}
						/>
					) : (
						<div className="mb-50"></div>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}

export default Activities;
